import React, { useState, useRef, useEffect } from 'react';
import { addDays, format } from 'date-fns';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { useTranslation } from 'react-i18next';
import { getComprehensiveStockReport, getAllGroceries } from '../API/stockReports';
import Skeleton from 'react-loading-skeleton';

function StockLogsReport() {
   const { t } = useTranslation();
   
   // State management
   const [selectedRange, setSelectedRange] = useState([
      {
         startDate: new Date(),
         endDate: addDays(new Date(), 7),
         key: 'selection',
      },
   ]);
   const [tempRange, setTempRange] = useState(selectedRange);
   const [showPicker, setShowPicker] = useState(false);
   const [selectedGrocery, setSelectedGrocery] = useState('');
   const [sortBy, setSortBy] = useState('date');
   const [sortOrder, setSortOrder] = useState('desc');
   
   // Data states
   const [stockData, setStockData] = useState(null);
   const [groceries, setGroceries] = useState([]);
   const [isLoading, setIsLoading] = useState(false);
   const [error, setError] = useState(null);
   
   const pickerRef = useRef(null);

   // Load groceries for filter dropdown
   useEffect(() => {
      const loadGroceries = async () => {
         try {
            const response = await getAllGroceries();
            if (response.data && response.data.success) {
               setGroceries(response.data.data || []);
            }
         } catch (err) {
            console.error('Error loading groceries:', err);
         }
      };
      loadGroceries();
   }, []);

   // Load stock report data
   useEffect(() => {
      loadStockReport();
   }, [selectedRange, selectedGrocery, sortBy, sortOrder]);

   const loadStockReport = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
         const params = {
            startDate: format(selectedRange[0].startDate, 'yyyy-MM-dd'),
            endDate: format(selectedRange[0].endDate, 'yyyy-MM-dd'),
         };
         
         if (selectedGrocery) {
            params.groceryId = selectedGrocery;
         }
         
         const response = await getComprehensiveStockReport(params);
         
         if (response.data && response.data.success) {
            setStockData(response.data.data);
         } else {
            setError('Failed to load stock report data');
         }
      } catch (err) {
         console.error('Error loading stock report:', err);
         setError('Error loading stock report. Please try again.');
      } finally {
         setIsLoading(false);
      }
   };

   useEffect(() => {
      const handleClickOutside = (event) => {
         if (pickerRef.current && !pickerRef.current.contains(event.target)) {
            setShowPicker(false);
            setTempRange(selectedRange);
         }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
   }, [selectedRange]);

   const handleApply = () => {
      setSelectedRange(tempRange);
      setShowPicker(false);
   };

   const handleClose = () => {
      setTempRange(selectedRange);
      setShowPicker(false);
   };

   const formattedRange = `${format(selectedRange[0].startDate, 'MMM d, yyyy')} - ${format(selectedRange[0].endDate, 'MMM d, yyyy')}`;

   // Convert 24-hour format time to 12-hour format
   const formatTimeTo12Hour = (time24) => {
      if (!time24) return '--';
      
      try {
         const [hours, minutes] = time24.split(':');
         const hour24 = parseInt(hours, 10);
         const minute = parseInt(minutes, 10);
         
         if (isNaN(hour24) || isNaN(minute)) return time24;
         
         const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
         const ampm = hour24 >= 12 ? 'PM' : 'AM';
         const minuteStr = minute.toString().padStart(2, '0');
         
         return `${hour12}:${minuteStr} ${ampm}`;
      } catch (error) {
         console.error('Error formatting time:', error);
         return time24;
      }
   };

   const getActionIcon = (action) => {
      switch (action) {
         case 'add':
            return (
               <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
               </svg>
            );
         case 'subtract':
            return (
               <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
               </svg>
            );
         case 'adjust':
            return (
               <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
               </svg>
            );
         default:
            return (
               <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
               </svg>
            );
      }
   };

   const getActionColor = (action) => {
      switch (action) {
         case 'add':
            return 'bg-green-100 text-green-800 border-green-200';
         case 'subtract':
            return 'bg-red-100 text-red-800 border-red-200';
         case 'adjust':
            return 'bg-blue-100 text-blue-800 border-blue-200';
         default:
            return 'bg-gray-100 text-gray-800 border-gray-200';
      }
   };

   return (
      <div className='mt-7'>
         {/* Enhanced Filters Section */}
         <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-semibold text-gray-800">{t('Stock_Logs_Filters')}</h3>
               <div className="flex items-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                  </svg>
                  <span>{t('Filter_stock_logs_by_date_range_and_item')}</span>
               </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
               {/* Date Range Picker */}
               <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                     <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                     {t('Date_Range')}
                  </label>
                  <div className="relative">
                     <div
                        onClick={(e) => {
                           e.stopPropagation();
                           setShowPicker((prev) => !prev);
                        }}
                        className="flex items-center justify-between w-full px-4 py-3 border border-gray-300 rounded-lg bg-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 cursor-pointer"
                     >
                        <div className="flex items-center">
                           <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                           </svg>
                           <span className={formattedRange ? "text-gray-900" : "text-gray-500"}>
                              {formattedRange || t('Select_date_range')}
                           </span>
                        </div>
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                     </div>
                     {showPicker && (
                        <div
                           ref={pickerRef}
                           className="absolute left-0 z-50 bg-white shadow-xl p-6 rounded-lg mt-2 border border-gray-200"
                        >
                           <div className="w-auto overflow-hidden">
                              <DateRangePicker
                                 onChange={(item) => setTempRange([item.selection])}
                                 months={2}
                                 ranges={tempRange}
                                 direction="horizontal"
                                 showSelectionPreview={true}
                                 moveRangeOnFirstSelection={false}
                                 preventSnapRefocus={true}
                                 calendarFocus="backwards"
                              />
                              <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-gray-200">
                                 <button
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       handleClose();
                                    }}
                                    className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 font-medium"
                                 >
                                    {t('Cancel')}
                                 </button>
                                 <button
                                    onClick={handleApply}
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                                 >
                                    {t('Apply_Filter')}
                                 </button>
                              </div>
                           </div>
                        </div>
                     )}
                  </div>
               </div>

               {/* Grocery Filter */}
               <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                     <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                     </svg>
                     {t('Grocery_Item')}
                  </label>
                  <div className="relative">
                     <select
                        value={selectedGrocery}
                        onChange={(e) => setSelectedGrocery(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
                     >
                        <option value="">{t('All_Groceries')}</option>
                        {groceries.map((grocery) => (
                           <option key={grocery._id} value={grocery._id}>
                              {grocery.name}
                           </option>
                        ))}
                     </select>
                     <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                     </div>
                  </div>
               </div>

               {/* Sort Options */}
               <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                     <svg className="w-4 h-4 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                     </svg>
                     {t('Sort_By')}
                  </label>
                  <div className="flex gap-2">
                     <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
                     >
                        <option value="date">{t('Date')}</option>
                        <option value="groceryName">{t('Item_Name')}</option>
                        <option value="quantity">{t('Quantity')}</option>
                        <option value="action">{t('Action_Type')}</option>
                     </select>
                     {/* <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value)}
                        className="px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white appearance-none cursor-pointer"
                     >
                        <option value="desc">{t('Descending')}</option>
                        <option value="asc">{t('Ascending')}</option>
                     </select> */}
                  </div>
               </div>
            </div>
            
            {/* Filter Summary */}
            <div className="mt-4 pt-4 border-t border-gray-200">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                     <span className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span>{t('Date')}: {formattedRange}</span>
                     </span>
                     <span className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span>{t('Item')}: {selectedGrocery ? groceries.find(g => g._id === selectedGrocery)?.name || 'Selected' : t('All_Groceries')}</span>
                     </span>
                  </div>
                  <div className="text-sm text-gray-500">
                     {stockData && stockData.logs ? `${stockData.logs.length} ${t('logs_found')}` : t('No_logs_found')}
                  </div>
               </div>
            </div>
         </div>

         {/* Summary Cards */}
         {/* {stockData && stockData.summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
               <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                     <div>
                        <p className="text-sm font-medium text-blue-600">{t('Total_Purchases')}</p>
                        <p className="text-2xl font-bold text-blue-900">{stockData.summary.purchases?.totalPurchases || 0}</p>
                     </div>
                     <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                     </div>
                  </div>
               </div>

               <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                     <div>
                        <p className="text-sm font-medium text-green-600">{t('Total_Usage')}</p>
                        <p className="text-2xl font-bold text-green-900">{stockData.summary.usage?.totalUsages || 0}</p>
                     </div>
                     <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
                        </svg>
                     </div>
                  </div>
               </div>

               <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                     <div>
                        <p className="text-sm font-medium text-purple-600">{t('Net_Change')}</p>
                        <p className="text-2xl font-bold text-purple-900">{stockData.summary.netChange?.quantityChange || 0}</p>
                     </div>
                     <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                     </div>
                  </div>
         </div>

               <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
         <div>
                        <p className="text-sm font-medium text-orange-600">{t('Total_Value')}</p>
                        <p className="text-2xl font-bold text-orange-900">₹{stockData.summary.netChange?.valueChange || 0}</p>
                     </div>
                     <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                     </div>
                  </div>
               </div>
            </div>
         )} */}

         {/* Enhanced Table */}
         <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
               <div className="grid grid-cols-10 gap-4 px-6 py-4">
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                     </svg>
                     {t('no')}
                  </div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                     </svg>
                     {t('Item_Name')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                     {t('Action')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2" />
                     </svg>
                     {t('Quantity')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round"  strokeLinejoin="round" strokeWidth={2} d="M6 4h12M6 8h12M6 8a4 4 0 0 1 4 4H6m0 0l6 8" />
                     </svg>
                     {t('Price')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round"  strokeLinejoin="round" strokeWidth={2} d="M6 4h12M6 8h12M6 8a4 4 0 0 1 4 4H6m0 0l6 8" />
                     </svg>
                     {t('Total_Amount')}
                  </div>
                  <div className="col-span-2 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                     </svg>
                     {t('Date')}
                  </div>
                  <div className="col-span-1 text-sm font-semibold text-gray-700 flex items-center">
                     <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                     </svg>
                     {t('Time')}
                  </div>
               </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-200">
               {isLoading ? (
                  <>
                     {Array.from({ length: 8 }, (_, index) => (
                        <div className="grid grid-cols-10 gap-4 px-6 py-4" key={index}>
                           <div className="col-span-10">
                              <Skeleton height={40} />
                           </div>
                        </div>
                     ))}
                  </>
               ) : error ? (
                  <div className="px-6 py-16 text-center">
                     <div className="flex flex-col items-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                           <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                           </svg>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">{t('Error_Loading_Data')}</h3>
                        <p className="text-gray-500 max-w-sm mb-4">{error}</p>
                        <button
                           onClick={loadStockReport}
                           className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
                        >
                           {t('Retry')}
                        </button>
                     </div>
                  </div>
               ) : (
                  <>
                     {stockData && stockData.logs && stockData.logs.length > 0 ? (
                        stockData.logs.map((log, index) => (
                           <div
                              key={index}
                              className="grid grid-cols-10 gap-4 px-6 py-4 hover:bg-gray-50 transition-colors duration-200 group"
                           >
                              <div className="col-span-1 flex items-center">
                                 <span className="text-sm font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                                    {index + 1}
                                 </span>
                              </div>
                              <div className="col-span-2 flex items-center">
                                 <div className="flex items-center">
                                    {/* <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                       <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                       </svg>
                                    </div> */}
                                    <span className="text-sm font-medium text-gray-900 truncate">
                                       {log.groceryName}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getActionColor(log.action)}`}>
                                    <div className="flex items-center gap-1">
                                       {getActionIcon(log.action)}
                                       <span className="capitalize">{log.action}</span>
                                    </div>
                                 </span>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <span className="text-sm font-semibold text-gray-900">
                                    {log.quantity} {log.unit}
                                 </span>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <span className="text-sm text-gray-600">
                                    ₹{log.purchasePrice || '0'}
                                 </span>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <span className="text-sm font-semibold text-gray-900">
                                    ₹{log.totalAmount || '0'}
                                 </span>
                              </div>
                              <div className="col-span-2 flex items-center">
                                 <div className="flex items-center">
                                    {/* <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg> */}
                                    <span className="text-sm text-gray-900">
                                       {format(new Date(log.date), 'MMM d, yyyy')}
                                    </span>
                                 </div>
                              </div>
                              <div className="col-span-1 flex items-center">
                                 <div className="flex items-center">
                                    {/* <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg> */}
                                    <span className="text-sm text-gray-900">
                                       {log.time ? formatTimeTo12Hour(log.time) : '--'}
                                    </span>
                                 </div>
                              </div>
                           </div>
                        ))
                     ) : (
                        <div className="px-6 py-16 text-center">
                           <div className="flex flex-col items-center">
                              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                 <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                 </svg>
                              </div>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('No_stock_logs_found')}</h3>
                              <p className="text-gray-500 max-w-sm">
                                 {t('No_stock_logs_match_filter_criteria')}
                              </p>
                           </div>
                        </div>
                     )}
                  </>
               )}
            </div>
         </div>
      </div>
   );
}

export default StockLogsReport;
